import type { Circuit, PlayerTrackPosition, TrackCell } from '../types/track.js';
import { TrackCellType } from '../types/track.js';
import { getPlayerTrackCell, stepTrackPosition } from './jokerLap.js';
import { computeInitialMoveBudget, type TrackMoveBonusFn } from './trackMovement.js';
import { resolveMoveBonus, type EffectiveRaceBonuses } from './raceBonuses.js';

/** Place affichée au-dessus du pion : `1e`, `2e`, … */
export function formatRaceRankLabel(rank: number): string | null {
  if (!Number.isInteger(rank) || rank < 1) return null;
  return `${rank}e`;
}

/** Teinte du bonus HUD = famille de case (alignée sur les pastilles course). */
export type CarHudBonusTone = 'straight' | 'turn' | 'start' | 'joker';

/**
 * Teinte selon la case actuelle : droite / virage / départ / joker.
 */
export function cellBonusTone(
  circuit: Circuit,
  position: PlayerTrackPosition
): CarHudBonusTone | null {
  const cell = getPlayerTrackCell(circuit, position);
  if (!cell) return null;
  if (position.startLaneIndex != null || cell.type === TrackCellType.START_LANE) {
    return 'start';
  }
  if (cell.type === TrackCellType.JOKER_LAP) return 'joker';
  if (cell.type === TrackCellType.TURN) return 'turn';
  if (cell.type === TrackCellType.START || position.mainPathIndex === 0) return 'start';
  if (cell.type === TrackCellType.STRAIGHT || cell.type === TrackCellType.JOKER) {
    return 'straight';
  }
  return 'straight';
}

/**
 * Bonus de la case actuelle pour l’affichage HUD (`+2`, `0`, …).
 * Utilise les mêmes règles que le budget de lancer (ligne / virage / départ / joker).
 */
export function cellBonusLabel(
  circuit: Circuit,
  position: PlayerTrackPosition,
  bonuses: EffectiveRaceBonuses
): string | null {
  const cell = getPlayerTrackCell(circuit, position);
  if (!cell) return null;
  const onStartLane = position.startLaneIndex != null;
  const value = resolveMoveBonus(cell.type, position.mainPathIndex, bonuses, onStartLane);
  if (!Number.isFinite(value)) return null;
  return value > 0 ? `+${value}` : '0';
}

/**
 * Trail du budget restant : index 0 = budget initial (avant 1er pas),
 * index i = budget après i pas (règle ÷2 à l’entrée en virage incluse).
 */
export function buildMoveBudgetTrail(
  circuit: Circuit,
  from: PlayerTrackPosition,
  jokerLapUsed: boolean,
  roll: number,
  getBonus: TrackMoveBonusFn
): number[] {
  const moveBudget = computeInitialMoveBudget(circuit, from, roll, getBonus);
  const trail: number[] = [moveBudget];
  let pos = from;
  let used = jokerLapUsed;
  let budget = moveBudget;
  const maxSteps =
    circuit.path.length +
    (circuit.jokerLap?.cells.length ?? 0) +
    (circuit.startLane?.cells.length ?? 0) +
    8;

  for (let guard = 0; guard < maxSteps && budget > 0; guard++) {
    const currentCell = getPlayerTrackCell(circuit, pos);
    if (!currentCell) break;
    const stepped = stepTrackPosition(circuit, pos, used);
    const nextCell = getPlayerTrackCell(circuit, stepped.position);
    if (!nextCell) break;

    const enteringTurnFromStraight =
      currentCell.type !== TrackCellType.TURN && nextCell.type === TrackCellType.TURN;

    if (enteringTurnFromStraight) {
      budget = Math.ceil(budget / 2);
      budget--;
    } else {
      budget--;
    }

    pos = stepped.position;
    used = stepped.jokerLapUsed;
    trail.push(Math.max(0, budget));
  }

  return trail;
}

export type CarHudOvertakePerspective = 'attacker' | 'defender';

export interface CarHudOvertakeState {
  attackerRoll: number;
  defenderRoll: number;
  success: boolean;
  perspective: CarHudOvertakePerspective;
  /** false tant que l’overlay n’a pas révélé le résultat (dés en « ? » orange). */
  revealed: boolean;
}

export interface CarHudBadgeInput {
  rank: number;
  cellBonus: string | null;
  cellBonusTone?: CarHudBonusTone | null;
  remainingBudget?: number | null;
  overtake?: CarHudOvertakeState | null;
  /** Nom affiché sous la place (replay / lisibilité). */
  driverName?: string | null;
}

export interface CarHudBadgeModel {
  rankLabel: string | null;
  cellBonus: string | null;
  cellBonusTone: CarHudBonusTone | null;
  remainingBudget: number | null;
  driverName: string | null;
  overtake: {
    myRoll: number;
    theirRoll: number;
    myOk: boolean;
    theirOk: boolean;
    revealed: boolean;
  } | null;
}

export function buildCarHudBadgeModel(input: CarHudBadgeInput): CarHudBadgeModel {
  const overtakeIn = input.overtake ?? null;
  let overtake: CarHudBadgeModel['overtake'] = null;
  if (overtakeIn) {
    const isAttacker = overtakeIn.perspective === 'attacker';
    const myOk = isAttacker ? overtakeIn.success : !overtakeIn.success;
    overtake = {
      myRoll: isAttacker ? overtakeIn.attackerRoll : overtakeIn.defenderRoll,
      theirRoll: isAttacker ? overtakeIn.defenderRoll : overtakeIn.attackerRoll,
      myOk,
      theirOk: !myOk,
      revealed: overtakeIn.revealed,
    };
  }
  const rawName = input.driverName?.trim() ?? '';
  return {
    rankLabel: formatRaceRankLabel(input.rank),
    cellBonus: input.cellBonus,
    cellBonusTone: input.cellBonusTone ?? null,
    remainingBudget:
      typeof input.remainingBudget === 'number' && Number.isFinite(input.remainingBudget)
        ? Math.max(0, Math.floor(input.remainingBudget))
        : null,
    driverName: rawName.length > 0 ? rawName : null,
    overtake,
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Markup compact pour le badge au-dessus d’une voiture (3D). */
export function renderCarHudBadgeHtml(model: CarHudBadgeModel): string {
  const parts: string[] = ['<div class="car-hud-badge">'];
  parts.push('<div class="car-hud-badge__row car-hud-badge__row--meta">');
  if (model.rankLabel) {
    parts.push(`<span class="car-hud-badge__rank">${escapeHtml(model.rankLabel)}</span>`);
  }
  if (model.cellBonus != null) {
    const toneClass = model.cellBonusTone ? ` car-hud-badge__bonus--${model.cellBonusTone}` : '';
    parts.push(
      `<span class="car-hud-badge__bonus${toneClass}">${escapeHtml(model.cellBonus)}</span>`
    );
  }
  parts.push('</div>');
  if (model.driverName) {
    parts.push(
      `<div class="car-hud-badge__row car-hud-badge__row--name"><span class="car-hud-badge__name">${escapeHtml(model.driverName)}</span></div>`
    );
  }
  if (model.remainingBudget != null) {
    parts.push(
      `<div class="car-hud-badge__row car-hud-badge__row--budget"><span class="car-hud-badge__budget">${model.remainingBudget}</span></div>`
    );
  }
  if (model.overtake) {
    if (!model.overtake.revealed) {
      parts.push(
        `<div class="car-hud-badge__row car-hud-badge__row--duel"><span class="car-hud-badge__die car-hud-badge__die--pending">?</span><span class="car-hud-badge__die car-hud-badge__die--pending">?</span></div>`
      );
    } else {
      const myClass = model.overtake.myOk
        ? 'car-hud-badge__die car-hud-badge__die--ok'
        : 'car-hud-badge__die car-hud-badge__die--fail';
      const theirClass = model.overtake.theirOk
        ? 'car-hud-badge__die car-hud-badge__die--ok'
        : 'car-hud-badge__die car-hud-badge__die--fail';
      parts.push(
        `<div class="car-hud-badge__row car-hud-badge__row--duel"><span class="${myClass}">${model.overtake.myRoll}</span><span class="${theirClass}">${model.overtake.theirRoll}</span></div>`
      );
    }
  }
  parts.push('</div>');
  return parts.join('');
}

/** Helper : getBonus pour trail / move depuis EffectiveRaceBonuses. */
export function raceBonusesToTrackMoveBonusFn(bonuses: EffectiveRaceBonuses): TrackMoveBonusFn {
  return (position: PlayerTrackPosition, cell: TrackCell) =>
    resolveMoveBonus(cell.type, position.mainPathIndex, bonuses, position.startLaneIndex != null);
}
